/**
 * Générateur — Il modélise une situation de proportionnalité à l'aide d'une
 * fonction linéaire.
 * Attendu ID : 218 | Niveau : 3ème | Domaine : Proportionnalité (d16)
 *
 * ⚠️ Mapping : le doc porte cet objectif en O6 du PDF OGD (séances B1) ; les
 * attendus 218 (d16) et 229 (d13) sont IDENTIQUES en base — remap manuel O6 →
 * 218 documenté au bootstrap (TODO_EXERCISEUR_3E.md §0). gen_229 (thème #7)
 * portera le versant FONCTIONS (séances B3), anti-doublon décidé au plan.
 *
 * Plan de cartes auto-validé 2026-07-16 (journal TODO_EXERCISEUR_3E.md §6) :
 *   « tableau »    — tableau de f : proportionnalité ? (QCM, bascule oui/non)
 *   « expression » — du tableau proportionnel à f(x) = ax (trou)
 *   « situations » — modélisable par une fonction linéaire ? (V/F, table de
 *                    situations à vérité ÉTABLIE — savoir standard)
 *   « procede »    — du procédé à la formule (coefficient en trou)
 *   « ratio »      — partages dans un ratio (2 ou 3 parts, totaux divisibles)
 *   « evolution »  — +p % = × coef : f(x) = coef·x (le pont O6↔O7, Créé n°8)
 */

import {
    randomChoice, randInt, shuffle, tex,
    makeQcm, makeTexteTrou, makeVraiFaux,
} from '../../../utils';

const ATTENDU_ID = 218;

// Contrainte d'écriture des coefficients (§0.1) : en phrase, pas en parenthèse.
const CONSIGNE_DEC = ' Écris le coefficient en écriture décimale.';

const L_TABLEAU = 'Reconnaître un tableau de fonction linéaire';
const L_EXPRESSION = 'Trouver l\'expression f(x) = ax d\'un tableau';
const L_SITUATIONS = 'Reconnaître les situations modélisables';
const L_PROCEDE = 'Écrire la fonction linéaire d\'un procédé';
const L_RATIO = 'Partager dans un ratio donné';
const L_EVOLUTION = 'Une évolution en pourcentage est une fonction linéaire';

const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const T_TH = 'background:#1E3A8A;color:#fff;padding:5px 10px;border:1px solid #64748b;font-weight:600';
const T_TD = 'padding:5px 10px;border:1px solid #64748b;text-align:center';
const tableXY = (xs, ys, nomF) => `<table style="border-collapse:collapse;margin:12px auto">`
    + `<tr><th style="${T_TH}">x</th>${xs.map(x => `<td style="${T_TD}">${x}</td>`).join('')}</tr>`
    + `<tr><th style="${T_TH}">${nomF}(x)</th>${ys.map(y => `<td style="${T_TD}">${y}</td>`).join('')}</tr></table>`;

const frNb = (x) => String(x).replace('.', ',');
// Coefficients décimaux « propres » (produit par un x entier → fini court).
const COEFS = [0.5, 1.5, 2, 2.5, 3, 3.5, 4, 5, 6, 7.5, 8];

// ═════════ « tableau » — proportionnalité ? (QCM) ═════════

const genTableauQcm = () => {
    const a = randomChoice(COEFS);
    const xs = shuffle([2, 4, 5, 8, 10, 12, -6, -4]).slice(0, 3);
    let ys = xs.map(x => x * a);
    const lineaire = randInt(0, 1) === 1;
    if (!lineaire) ys = ys.map((y, i) => (i === 2 ? y + randomChoice([xs[2], -xs[2]]) : y));
    const nomF = randomChoice(['f', 'g', 'h']);
    const quotient = tex(`\\dfrac{${nomF}(x)}{x}`);
    const chOui = `Oui : tous les quotients ${quotient} sont égaux à ${frNb(a)}.`;
    const chNon = 'Non : un des quotients diffère des autres.';
    const chOuiFaux = `Oui : tous les quotients ${quotient} sont égaux à ${frNb(a + 1)}.`;
    const correct = lineaire ? chOui : chNon;
    const payload = makeQcm([chOui, chNon, chOuiFaux], correct,
        `<p>Voici un tableau de valeurs de la fonction ${nomF} :</p>`
        + tableXY(xs.map(frNb), ys.map(frNb), nomF)
        // L'équivalence « tableau de proportionnalité ⟺ fonction linéaire » est
        // le pont que cette carte fait construire : la donner d'avance en fait
        // une simple vérification de quotients.
        + '<p><b>Ce tableau est-il un tableau de proportionnalité ?</b></p>',
        opts(L_TABLEAU));
    payload._meta.params = { a, xs, ys, lineaire };
    return payload;
};

// ═════════ « expression » — f(x) = ax (trou) ═════════

const genExpressionTrous = () => {
    const a = randomChoice(COEFS);
    const xs = shuffle([2, 4, 5, 8, 10, -6, -4]).slice(0, 3);
    const ys = xs.map(x => x * a);
    const nomF = randomChoice(['f', 'g', 'h']);
    const phrase = `$${nomF}(x) = {{${frNb(a)}}}x$`;
    const payload = makeTexteTrou(phrase,
        `<p>Le tableau de la fonction ${nomF} est un tableau de proportionnalité :</p>`
        + tableXY(xs.map(frNb), ys.map(frNb), nomF)
        + `<p><b>Donne l'expression algébrique de la fonction.</b>${CONSIGNE_DEC}</p>`,
        { ...opts(L_EXPRESSION), mode: 'saisie' });
    payload._meta.params = { a, xs, ys };
    return payload;
};

// ═════════ « situations » — modélisable ? (V/F à vérité établie) ═════════

// Vérités STANDARD (savoir de cours, répliquées côté solveur).
const SITUATIONS = [
    ['La taille d\'une personne en fonction de son âge.', false],
    ['Le prix des tomates en fonction de leur masse, à prix au kilo fixe.', true],
    ['Le temps de cuisson des pâtes en fonction de leur masse.', false],
    ['La distance parcourue en fonction du temps, à vitesse constante.', true],
    ['Le périmètre d\'un carré en fonction de son côté.', true],
    ['L\'aire d\'un carré en fonction de son côté.', false],
    ['Le montant payé en fonction du nombre de places de cinéma, à tarif unique.', true],
    ['La température extérieure en fonction de l\'heure de la journée.', false],
];

const genSituationsVf = () => {
    let tirage = shuffle(SITUATIONS).slice(0, 4);
    if (tirage.every(([, v]) => v) || tirage.every(([, v]) => !v)) {
        // Mix garanti : on force une situation de l'autre vérité.
        const autre = SITUATIONS.find(([, v]) => v !== tirage[0][1]);
        tirage = [...tirage.slice(0, 3), autre];
    }
    const payload = makeVraiFaux(tirage.map(s => s[0]), tirage.map(s => (s[1] ? 0 : 1)),
        // « Vrai si la grandeur est proportionnelle » donnait le critère de
        // décision, qui est exactement ce que la carte évalue.
        '<p><b>Ces situations peuvent-elles être modélisées par une fonction LINÉAIRE ?</b></p>',
        opts(L_SITUATIONS));
    payload._meta.params = { tirage };
    return payload;
};

// ═════════ « procede » — du procédé à la formule ═════════

const genProcedeTrous = () => {
    const famille = randomChoice(['prix', 'vitesse', 'perimetre']);
    let phrase; let texte;
    // « (complète le coefficient) » : le gabarit de la phrase le montre déjà.
    const CONSIGNE = '<p><b>Exprime la fonction associée.</b></p>';
    if (famille === 'prix') {
        const u = randomChoice([12, 15, 18, 22, 25, 35]);
        const objet = randomChoice(['paires de chaussettes', 'places de concert', 'bandes dessinées']);
        phrase = `$p = {{${u}}}n$`;
        texte = `<p>Le montant p, en euros, de n ${objet} à ${u} € l'unité est proportionnel à n.</p>${CONSIGNE}`;
    } else if (famille === 'vitesse') {
        const v = randomChoice([50, 80, 90, 100, 110, 130]);
        phrase = `$d = {{${v}}}t$`;
        texte = `<p>À la vitesse constante de ${v} km/h, la distance d, en kilomètres, est `
            + `proportionnelle au temps t, en heures.</p>${CONSIGNE}`;
    } else {
        const n = randomChoice([3, 4, 5, 6, 8]);
        const NOMS = { 3: 'triangle équilatéral', 4: 'carré', 5: 'pentagone régulier', 6: 'hexagone régulier', 8: 'octogone régulier' };
        phrase = `$p = {{${n}}}c$`;
        texte = `<p>Le périmètre p d'un ${NOMS[n]} est proportionnel à son côté c.</p>${CONSIGNE}`;
    }
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_PROCEDE), mode: 'saisie' });
    payload._meta.params = { famille };
    return payload;
};

// ═════════ « ratio » — partages ═════════

const genRatioTrous = () => {
    if (randInt(0, 1)) {
        // Ratio a:b, total T divisible par a+b.
        let a; let b;
        do { a = randInt(1, 5); b = randInt(2, 9); } while (a === b);
        const T = (a + b) * randomChoice([3, 4, 5, 8, 10]);
        const [ing1, ing2, boisson, unite] = randomChoice([
            ['sirop', 'limonade', 'diabolo', 'cL'], ['jus d\'orange', 'eau pétillante', 'cocktail', 'cL'],
            ['cacao', 'lait', 'chocolat chaud', 'cL'],
        ]);
        const q1 = (T * a) / (a + b); const q2 = (T * b) / (a + b);
        const de = (mot) => (/^[aeéiouh]/.test(mot) ? `d'${mot}` : `de ${mot}`);   // élision
        const phrase = `Il faut {{${q1}}} ${unite} ${de(ing1)} et {{${q2}}} ${unite} ${de(ing2)}.`;
        const payload = makeTexteTrou(phrase,
            `<p>Un ${boisson} est composé de ${ing1} et de ${ing2} dans le ratio ${a}:${b}.</p>`
            + `<p><b>Quelles sont les quantités dans un ${boisson} de ${T} ${unite} ?</b></p>`,
            { ...opts(L_RATIO), mode: 'saisie' });
        payload._meta.params = { a, b, T };
        return payload;
    }
    // « x fois plus » → ratio à 3 parts (le jeu vidéo).
    let k1; let k2;
    do { k1 = randInt(2, 5); k2 = randInt(2, 4); } while (k1 === k2);
    // Mario = k1 × Gébril et Mario = k2 × Lily → ratio M:L:G = k1·k2 : k1 : k2.
    const parts = [k1 * k2, k1, k2];
    const S = parts[0] + parts[1] + parts[2];
    const T = S * randomChoice([31, 62, 124, 248]);
    const [p1, p2, p3] = randomChoice([['Mario', 'Lily', 'Gébril'], ['Ana', 'Bilal', 'Cléo'], ['Rémi', 'Sonia', 'Tino']]);
    const phrase = `${p1} : {{${(T * parts[0]) / S}}} points ; ${p2} : {{${(T * parts[1]) / S}}} points ; ${p3} : {{${(T * parts[2]) / S}}} points.`;
    const payload = makeTexteTrou(phrase,
        `<p>Dans un jeu vidéo, ${p1} gagne ${k1} fois plus de points que ${p3} et ${k2} fois plus que ${p2}. `
        + `À eux trois, ils ont accumulé ${T} points.</p>`
        // Le ratio pré-calculé était la moitié du travail : c'est le passage des
        // « x fois plus » au ratio à trois parts que la carte évalue.
        + '<p><b>Combien de points chacun a-t-il gagnés ?</b></p>',
        { ...opts(L_RATIO), mode: 'saisie' });
    payload._meta.params = { k1, k2, parts, T };
    return payload;
};

// ═════════ « evolution » — +p % = × coef (le pont) ═════════

const genEvolutionTrous = () => {
    const hausse = randInt(0, 1) === 1;
    const p = randomChoice(hausse ? [5, 10, 20, 25, 30, 40, 50] : [5, 10, 20, 25, 30, 40]);
    const coefCent = hausse ? 100 + p : 100 - p;           // coef × 100
    const coef = frNb(coefCent % 100 === 0 ? coefCent / 100 : coefCent / 100);
    // Prix initial choisi pour un résultat EXACT au centime : multiple de 4 €.
    const prix = randomChoice([12, 16, 20, 24, 28, 32, 36, 40, 44, 48]);
    const nouveauCent = prix * coefCent;                   // en centimes d'euro
    // SANS zéro final (« 12,6 », jamais « 12,60 ») : sinon « 12,6 » saisi = faux.
    const nouveau = nouveauCent % 100 === 0 ? String(nouveauCent / 100)
        : (nouveauCent % 10 === 0 ? `${Math.floor(nouveauCent / 100)},${(nouveauCent % 100) / 10}`
            : `${Math.floor(nouveauCent / 100)},${String(nouveauCent % 100).padStart(2, '0')}`);
    const objet = randomChoice(['un abonnement', 'un jeu', 'un livre', 'un sac']);
    const phrase = `$\\text{Le prix est multiplié par } {{${coef}}} \\qquad f(x) = {{${coef}}}x`
        + ` \\qquad \\text{le nouveau prix est } {{${nouveau}}} \\text{ €}$`;
    const payload = makeTexteTrou(phrase,
        `<p>Le prix ${objet.replace('un ', 'd\'un ')} ${hausse ? 'augmente' : 'baisse'} de ${p} %. `
        + `On note x l'ancien prix et ${tex('f(x)')} le nouveau prix. `
        + `${objet.charAt(0).toUpperCase() + objet.slice(1)} coûtait ${prix} €.</p>`
        // « La fonction f est LINÉAIRE : une évolution en pourcentage est une
        // multiplication » est le pont que l'exercice fait construire.
        + `<p><b>Complète.</b>${CONSIGNE_DEC}</p>`,
        { ...opts(L_EVOLUTION), mode: 'saisie' });
    payload._meta.params = { hausse, p, prix, coef, nouveau };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    tableau_qcm_unique: genTableauQcm,
    expression_texte_trou_saisie: genExpressionTrous,
    situations_tableau_vf: genSituationsVf,
    procede_texte_trou_saisie: genProcedeTrous,
    ratio_texte_trou_saisie: genRatioTrous,
    evolution_texte_trou_saisie: genEvolutionTrous,
};

const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM', tableau_vf: 'Vrai/Faux' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'tableau', label: L_TABLEAU, formatsList: buildFormats('tableau') },
    { id: 'expression', label: L_EXPRESSION, formatsList: buildFormats('expression') },
    { id: 'situations', label: L_SITUATIONS, formatsList: buildFormats('situations') },
    { id: 'procede', label: L_PROCEDE, formatsList: buildFormats('procede') },
    { id: 'ratio', label: L_RATIO, formatsList: buildFormats('ratio') },
    { id: 'evolution', label: L_EVOLUTION, formatsList: buildFormats('evolution') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
